/**
 * Which account-maintenance actions an administrator has switched on (Bill 2026-10-17: "turn off by admin toggle").
 * GET v3/maintenance/actions. Everything is off until the switches are turned on; while loading or on any error the
 * answer is "off" (nothing new appears, and the older direct-to-Core dialogs stay hidden).
 */
import { useEffect, useState } from 'react';
import type { MaintenanceActionsResponse } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';

export type MaintenanceSwitches = Pick<MaintenanceActionsResponse, 'enabled' | 'actions' | 'profileEdit' | 'legacyRcmActions'> & { testCustomersOnly: boolean } & { loaded: boolean };

const OFF: MaintenanceSwitches = { enabled: false, actions: [], profileEdit: false, legacyRcmActions: false, testCustomersOnly: true, loaded: false };

let cached: { at: number; value: Promise<MaintenanceSwitches> } | null = null;
const TTL_MS = 60_000; // an administrator's change shows within a minute

export function useMaintenanceSwitches(): MaintenanceSwitches {
  const client = useApiClient();
  const [state, setState] = useState<MaintenanceSwitches>(OFF);
  useEffect(() => {
    let live = true;
    if (!cached || Date.now() - cached.at > TTL_MS) {
      cached = {
        at: Date.now(),
        value: client.retail.maintenance.actions()
          .then((r) => ({ enabled: !!r.enabled, actions: r.actions ?? [], profileEdit: !!r.profileEdit, legacyRcmActions: !!r.legacyRcmActions, testCustomersOnly: r.testCustomersOnly !== false, loaded: true }))
          .catch(() => { cached = null; return { ...OFF, loaded: true }; }),
      };
    }
    void cached.value.then((v) => { if (live) setState(v); });
    return () => { live = false; };
  }, [client]);
  return state;
}

/** Shown when the record screen holds a real customer while maint.testCustomersOnly is on (Bill 2026-10-17). */
export const TEST_GATE_MESSAGE =
  'Test gate is on: changes to existing customers are limited to test customers for now (last name Test with a 666 SSN/TIN or CIF). This customer is not a test customer, so nothing can be changed here.';
